import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, FileText, Download, Upload, Link2, Copy, Trash2, X, Cpu, Wrench, ScanSearch, Hash, AlertTriangle, CheckCircle2, HardDriveDownload, RotateCcw } from "lucide-react";
import { YAMAHA_PROTOS, ecuProto, ecuIdxByProto, HW_TO_ECU, AUTO_CANDIDATES, FULL_DUMP_SIZES, yamahaScanBus } from "../flashtools/lib/yamahaEngine";
import { YAMAHA_ECUS, YAMAHA_IFACES, YAMAHA_ENGINE_URL, ecuFromBin, pingYamahaEngine, yamahaInfo, yamahaCrc, yamahaVerifyMap, yamahaReadBack, yamahaWrite, yamahaFinalize, yamahaResetFlag, yamahaRecover, yamahaLive, yamahaClearDtc, yamahaProbeRead, yamahaReadCan, yamahaScanPids, yamahaYmeter, yamahaDevices } from "../flashtools/lib/yamahaEngine";
import { YAMAHA_PART_DB } from "../flashtools/lib/yamahaPartDb";
import { api } from "../lib/api";
import { crc32 } from "../flashtools/lib/firmware";
import { isMotoFile, decryptMoto, encryptMoto } from "../flashtools/lib/motoFile";
import { getSharedManager } from "../flashtools/lib/serial";
import { fetchBridgePorts } from "../flashtools/lib/bridge";
import { YamahaDiag } from "../flashtools/components/YamahaDiag";
import { YamahaSensorTable, YamahaDtcCatalog } from "../flashtools/components/YamahaDiagTables";

const BRIDGE_URL = "ws://localhost:8765";
// Versi engine native yang dipaket di /public — dipakai untuk cache-busting link download
// dan untuk memperingatkan kalau engine yang jalan di PC ternyata versi lama.
const ENGINE_VER = "2.2.3";

const now = () => new Date().toTimeString().slice(0, 8);
const hex4 = (n) => (n & 0xffff).toString(16).toUpperCase().padStart(4, "0");// Diagnostik: varian rumus checksum file, untuk dicocokkan dengan nilai rutin 31 02 dari ECU.
const sumVariants = (b) => {
  let s8 = 0, be = 0, le = 0;
  for (let i = 0; i < b.length; i++) s8 += b[i];
  for (let i = 0; i + 1 < b.length; i += 2) { be += (b[i] << 8) | b[i + 1]; le += (b[i + 1] << 8) | b[i]; }
  return {
    sum8: hex4(s8),
    "sum8+carry": hex4((s8 & 0xffff) + (s8 >>> 16)),
    "~sum8": hex4(~s8),
    "-sum8": hex4(0x10000 - (s8 & 0xffff)),
    sum16BE: hex4(be),
    sum16LE: hex4(le),
    // Σ16BE = AA55 adalah target self-consistent khas Denso: image yang checksum-nya benar selalu AA55.

  };
};


const PHASE_TXT = {
  open: "Membuka interface J2534…",
  detect: "Deteksi K-Line (IG tetap ON)…",
  ignition_off: "🔻 MATIKAN IG / POWER 12V OFF SEKARANG (tunggu perintah ON)",
  ignition_on: "🔑 NYALAKAN IG / POWER 12V ON SEKARANG",
  wake: "Wake-up / fast init…",
  ignition_cycle: "🔑 IG OFF → tunggu 2 detik → IG ON",
  wake_ok: "ECU merespon ✓",
  security: "Security access (seed-key)…",
  live: "Streaming live data…",
  erase: "Erasing flash… (bisa 1–5 menit, JANGAN matikan IG)",
  download: "Request download…",
  write: "WRITE…",
  verify: "Verify / checksum routine…",
  done: "Finalizing ✓",
};

const TABS = [
  { id: "remap", label: "REMAP", icon: Cpu },
  { id: "diag", label: "DIAGNOSTIC", icon: ScanSearch },
  { id: "info", label: "INFO ECU", icon: Hash },
];

const PanelHead = ({ icon, title, right }) => (
  <div className="relative flex items-stretch rounded-t-md overflow-hidden bg-[#06182e] border-b border-sky-800/60">
    <div className="flex items-center gap-3 pl-5 pr-14 py-2 bg-gradient-to-b from-[#1d7fe0] to-[#1159a8]" style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 24px) 100%, 0 100%)" }}>
      <span className="text-white flex items-center">{icon}</span>
      <h2 className="text-white font-black tracking-wide uppercase text-[21px]">{title}</h2>
    </div>
    <div className="w-3 -ml-6 bg-sky-400/50" style={{ clipPath: "polygon(70% 0, 100% 0, 30% 100%, 0 100%)" }} />
    {right ? <div className="ml-auto flex items-stretch">{right}</div> : <div aria-hidden className="ml-auto w-12 bg-sky-600" style={{ clipPath: "polygon(100% 0, 100% 100%, 0 0)" }} />}
  </div>
);

const Row = ({ label, value, testid }) => (
  <div className="flex items-center gap-3">
    <span className="w-36 shrink-0 text-[16px] font-medium text-white">{label}</span>
    <input data-testid={testid} value={value ?? ""} readOnly
      className="flex-1 min-w-0 h-8 text-[14px] px-3 rounded-[3px] border border-[#6b7280] bg-[#101722] text-white font-mono outline-none" />
  </div>
);

const Panel = ({ children, className = "" }) => (
  <div className={`rounded-md border-2 border-[#3b4756] bg-gradient-to-b from-[#0f151d] to-[#0a0d13] shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_8px_24px_rgba(0,0,0,0.5)] ${className}`}>{children}</div>
);

export function YamahaFlashtoolsPanel({ onClose }) {
  const navigate = useNavigate();
  const manager = getSharedManager();
  const fileRef = useRef(null);
  const logBoxRef = useRef(null);

  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("remap");
  const [iface, setIface] = useState("sm3");
  const [ecuIdx, setEcuIdx] = useState(0);
  const [proto, setProto] = useState("kline");
  const [firmware, setFirmware] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [procStatus, setProcStatus] = useState("");
  const [mapChunks, setMapChunks] = useState(8);
  const [engineOnline, setEngineOnline] = useState(null);
  const [engineVer, setEngineVer] = useState("");
  const [ecuInfo, setEcuInfo] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [flashResult, setFlashResult] = useState(null);
  const [writeTs, setWriteTs] = useState(0);
  const [infoTs, setInfoTs] = useState(0);
  const [ports, setPorts] = useState([]);
  const [comPort, setComPort] = useState("");
  const [hwQuery, setHwQuery] = useState("");
  const [liveFrames, setLiveFrames] = useState([]);
  const [sensorItems, setSensorItems] = useState({});
  const [infoEcuIdx, setInfoEcuIdx] = useState(null);
  const [livePid, setLivePid] = useState("01");
  const [devices, setDevices] = useState([]);
  const autoDoneRef = useRef(false);

  const log = useCallback((level, msg) => setLogs((l) => [...l, { level, text: `[${now()}] ${String(msg).replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "")}` }].slice(-800)), []);
  const askConfirm = useCallback((message) => new Promise((resolve) => setConfirmState({ message, resolve })), []);
  const onPhase = useCallback((m) => {
    if (!m) return;
    if (m.phase) { setProcStatus(PHASE_TXT[m.phase] || String(m.phase).toUpperCase()); log("INFO", PHASE_TXT[m.phase] || `Fase: ${m.phase}`); }
    else if (m.msg) log("INFO", String(m.msg));
  }, [log]);
  const resolveConfirm = useCallback((v) => setConfirmState((c) => { if (c) c.resolve(v); return null; }), []);

  useEffect(() => { if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight; }, [logs]);

  const checkEngine = useCallback(async () => {
    const r = await pingYamahaEngine();
    setEngineOnline(!!r.online);
    setEngineVer(r.version || "");
    log(r.online ? "SUCCESS" : "WARN", r.online
      ? `TRMD Yamaha Engine online (v${r.version || "?"}) · ${YAMAHA_ENGINE_URL}`
      : `TRMD Yamaha Engine tidak terdeteksi di ${YAMAHA_ENGINE_URL} — jalankan TRMD Yamaha Engine di PC Windows.`);
    return r;
  }, [log]);

  const detectDevices = useCallback(async () => {
    try {
      const d = await yamahaDevices();
      if (!d || d.ok === false) { log("WARN", `Auto-detect device gagal: ${d?.error || "tidak ada balasan engine"}`); return; }
      const list = String(d.devices || "").split("|").filter(Boolean).map((x) => {
        const [kind, name, present, online] = x.split(";");
        return { kind, name, present: present === "1", online: online === "1" };
      });
      setDevices(list);
      if (!list.length) { log("WARN", "Tidak ada device J2534 terdaftar di Windows — install driver Tactrix Openport 2.0 atau Scanmatik."); return; }
      autoDoneRef.current = true;
      list.forEach((x) => log("INFO", `Device J2534: ${x.name} → ${YAMAHA_IFACES.find((i) => i.id === x.kind)?.label || x.kind}${x.online ? " · TERCOLOK ✓" : x.present ? " (driver ada, device tidak merespons)" : " (DLL tidak ditemukan)"}`));
      const onlineDev = list.find((x) => x.online && x.kind !== "sm3wifi");
      if (onlineDev) {
        setIface(onlineDev.kind);
        log("SUCCESS", `Interface dipilih otomatis (device tercolok): ${YAMAHA_IFACES.find((i) => i.id === onlineDev.kind)?.label || onlineDev.kind}`);
      } else if (d.auto) {
        setIface(d.auto);
        log("WARN", `Tidak ada device yang bisa dibuka — pakai driver terdaftar: ${YAMAHA_IFACES.find((i) => i.id === d.auto)?.label || d.auto}`);
      }
    } catch (e) { log("WARN", `Auto-detect device gagal: ${e.message || e}`); }
  }, [log]);

  useEffect(() => { (async () => {
    const r = await checkEngine();
    if (r.online && !autoDoneRef.current) detectDevices();
  })(); }, [checkEngine, detectDevices]);

  useEffect(() => {
    if (tab !== "diag") return;
    let alive = true;
    (async () => {
      const r = await fetchBridgePorts(BRIDGE_URL);
      if (!alive) return;
      setPorts(r.ports || []);
      if (!comPort && r.ports && r.ports.length) setComPort(r.ports[0].port);
    })();
    return () => { alive = false; };
  }, [tab, comPort]);

  const ecu = YAMAHA_ECUS[ecuIdx];
  const protoList = useMemo(() => ecuIdxByProto(proto), [proto]);
  const expected = useMemo(() => (ecu ? ecu.sizes : []), [ecu]);
  const sizeOk = !!firmware && expected.includes(firmware.bytes.length);
  const flashCapacity = useMemo(() => {
    const m = /(\d+)\s*KB/i.exec(ecu?.label || "");
    if (!m) return "";
    const kb = parseInt(m[1], 10);
    return `${kb}KB`;
  }, [ecu]);

  const handleFile = useCallback(async (f) => {
    let buf = new Uint8Array(await f.arrayBuffer());
    if (isMotoFile(f.name)) {
      try {
        buf = await decryptMoto(buf.buffer);
        log("SUCCESS", `File terenkripsi didekripsi → ${buf.length} bytes BIN.`);
      } catch (e) { log("ERROR", `File gagal dibuka: ${e.message}`); return; }
    }
    const crc = crc32(buf);
    // Shannon entropy: file TUNE Yamaha yang siap flash sudah terenkripsi (±7.6 bit/byte);
    // dump plain/stock jauh lebih rendah (±6.3). Flashtool mengirim byte mentah, tidak mengenkripsi.
    const freq = new Array(256).fill(0);
    for (let i = 0; i < buf.length; i++) freq[buf[i]]++;
    let ent = 0;
    for (let i = 0; i < 256; i++) { if (freq[i]) { const p = freq[i] / buf.length; ent -= p * Math.log2(p); } }
    // M16C 54P: isi flash aslinya PLAIN — 2 byte signature lalu part code ASCII (mis. "54P01-120705-500-00").
    // Buang byte non-printable (file berisi 0x00 setelah part code; NUL memotong teks log).
    const ascii = String.fromCharCode(...buf.slice(0, 64)).replace(/[^\x20-\x7E]/g, " ");
    // Header part-code Yamaha: "54P01-120705-500-00" (M16C) atau "BKU00-220119-VER500P-0" (CAN).
    // Part-code: ambil token tepat sebelum "-YYMMDD-" (2 byte pertama file adalah signature, bukan teks).
    const m16cTag = /-?([0-9A-Z]{3,5})-([0-9]{6})-((VER)?[0-9]{3})/.exec(ascii);
    const plainM16c = !!m16cTag;
    setFirmware({ name: f.name, bytes: buf, crc, entropy: ent, plainOk: plainM16c, partTag: m16cTag ? m16cTag[1] : "", header: ascii.slice(0, 48) });
    log("INFO", `File: ${f.name} · ${buf.length} bytes · CRC32 ${crc} · entropi ${ent.toFixed(2)} bit/byte`);
    // Σ16BE = 0xAA55 adalah target checksum Denso (device-confirmed di B3F/MC9S12XS256 via rutin 31 02).
    const v = sumVariants(buf);
    if (v.sum16BE === "AA55")
      log("SUCCESS", `Σ16BE file = 0xAA55 — cocok dengan target checksum Denso, file valid/utuh ✓`);
    else
      log("INFO", `Σ16BE file = 0x${v.sum16BE} (target checksum Denso = 0xAA55; tipe ECU lain bisa beda rumus).`);
    if (plainM16c)
      log("INFO", `Header part-code Yamaha terbaca (${m16cTag[0]}) — ini dump PLAIN, bagus untuk dibaca/analisa, TAPI tidak boleh ditulis ke ECU.`);
    if (ent < 7.2)
      log("WARN", `Entropi ${ent.toFixed(2)} = file PLAIN/DUMP. TERBUKTI di perangkat (LEXY B3F, 15 Jun 2026): ECU ini mendekripsi data masuk dengan cipher blok 8 byte, jadi file plain menjadi pola acak berulang di flash. Butuh file TERENKRIPSI.`);
    else log("SUCCESS", `Entropi ${ent.toFixed(2)} — konsisten dengan file terenkripsi siap flash.`);
    const idx = ecuFromBin(buf);
    if (idx >= 0) { setEcuIdx(idx); setProto(ecuProto(YAMAHA_ECUS[idx])); log("SUCCESS", `Tipe ECU terdeteksi otomatis: ${YAMAHA_ECUS[idx].label}`); }
    else {
      const fd = FULL_DUMP_SIZES[buf.length];
      if (fd) {
        log("WARN", `Ukuran ${buf.length} B = ${fd.note}`);
        log("WARN", `File ini DUMP PENUH, bukan file siap-flash (${YAMAHA_ECUS[fd.ecu].label} butuh ${fd.payload} B). Pakai tombol POTONG PAYLOAD untuk membuat file siap-flash.`);
      } else log("WARN", "Ukuran file tidak cocok tipe ECU Yamaha mana pun — pilih tipe ECU manual.");
    }
  }, [log]);

  const changeProto = useCallback((p) => {
    setProto(p);
    const list = ecuIdxByProto(p);
    setEcuIdx((cur) => (list.includes(cur) ? cur : list[0]));
    log("INFO", `Protokol: ${YAMAHA_PROTOS.find((x) => x.id === p)?.label}`);
  }, [log]);

  const doAutoEcu = useCallback(async () => {
    setBusy(true); setProcStatus("AUTO DETECT ECU…"); setProgress(0);
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      const cands = AUTO_CANDIDATES[proto] || [];
      log("INFO", `AUTO DETECT (read-only) · protokol ${proto.toUpperCase()} · ${cands.length} kandidat tipe ECU.`);
      for (const idx of cands) {
        log("INFO", `Mencoba ${YAMAHA_ECUS[idx].label}… (ikuti perintah IG OFF / IG ON)`);
        let res = null;
        try { res = await yamahaInfo(iface, idx, onPhase); } catch (e) { res = { ok: false, error: e.message || e }; }
        const part = String(res?.info?.part || "").toUpperCase();
        if (!res || res.ok === false || !part) { log("WARN", `Tidak ada balasan untuk ${YAMAHA_ECUS[idx].label}.`); continue; }
        setEcuInfo(res.info);
        const mapped = HW_TO_ECU[part];
        const final = typeof mapped === "number" ? mapped : idx;
        setEcuIdx(final);
        setProto(ecuProto(YAMAHA_ECUS[final]));
        setInfoEcuIdx(final);
        log("SUCCESS", `ECU terhubung · Software Id ${part} · Part ${YAMAHA_PART_DB[part] || res.info.label || "Unknown"}`);
        log("SUCCESS", `Tipe ECU di-set otomatis: ${YAMAHA_ECUS[final].label}`);
        setProcStatus("AUTO DETECT selesai ✓");
        return;
      }
      log("ERROR", "Tidak ada ECU yang menjawab pada protokol ini — cek interface, kabel, dan coba protokol satunya.");
      setProcStatus("GAGAL ✗");
    } catch (e) { log("ERROR", `Auto detect gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [checkEngine, proto, iface, onPhase, log]);

  const doInfo = useCallback(async () => {
    setBusy(true); setProcStatus("READ INFO…"); setProgress(0);
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("INFO", `Membaca info ECU via ${YAMAHA_IFACES.find((x) => x.id === iface)?.label} · ${ecu.label}…`);
      log("INFO", "Urutan (sama seperti tool asli): tunggu perintah MATIKAN IG (OFF) ±9 detik → lalu NYALAKAN IG (ON) ±7 detik → engine lanjut fast-init otomatis.");
      setProcStatus("Deteksi K-Line (IG tetap ON)…");
      const res = await yamahaInfo(iface, ecuIdx, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      const info = res?.info || {};
      setEcuInfo(info);
      setInfoTs(Date.now());
      setInfoEcuIdx(ecuIdx);
      const part = (info.part || "").toUpperCase();
      const mapped = HW_TO_ECU[part];
      if (typeof mapped === "number" && mapped !== ecuIdx) {
        setEcuIdx(mapped); setProto(ecuProto(YAMAHA_ECUS[mapped])); setInfoEcuIdx(mapped);
        log("SUCCESS", `Tipe ECU di-set otomatis dari Software Id ${part}: ${YAMAHA_ECUS[mapped].label}`);
      }
      log("SUCCESS", `Software Id ${part || "—"} · Part ${YAMAHA_PART_DB[part] || info.label || "Unknown"}`);
      log("INFO", `Software Id ${info.part || "—"} · Version Number ${info.vendor || "—"} · Model Code ${info.soft || "—"} · Hardware Serial ${info.model || "—"} · Software Version ${info.ver || "—"} · VIN ${info.vinpid || "—"} · Reprogram Flag ${info.reflag || "—"}`);
      setProcStatus("READ INFO selesai ✓");
    } catch (e) { log("ERROR", `Read info gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [checkEngine, iface, ecuIdx, ecu, log]);

  const doCrc = useCallback(async () => {
    if (infoEcuIdx != null && infoEcuIdx !== ecuIdx) {
      const go = await askConfirm(`Tipe ECU dipilih: ${ecu.label}\nTipe saat READ INFO berhasil: ${YAMAHA_ECUS[infoEcuIdx].label}\n\nAlamat rutin checksum berbeda per tipe — kalau tipenya salah, ECU tidak akan menjawab.\n\nLanjut pakai tipe yang dipilih sekarang?`);
      if (!go) { log("WARN", "CRC dibatalkan — pilih tipe ECU yang sama dengan hasil READ INFO."); return; }
    }
    setBusy(true); setProcStatus("CRC CHECK…"); setProgress(0);
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      const size = firmware ? firmware.bytes.length : expected[0];
      log("INFO", `CRC check ECU · ${ecu.label} · ${size} bytes…`);
      const res = await yamahaCrc(iface, ecuIdx, size, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", `Checksum 16-bit ECU: 0x${res?.crc || "—"}`);
      if (firmware) {
        log("INFO", `CRC32 file lokal: ${firmware.crc}`);
        const v = sumVariants(firmware.bytes);
        log("INFO", `Varian checksum file: ${Object.entries(v).map(([k, x]) => `${k}=0x${x}`).join(" · ")}`);
        const hit = Object.entries(v).find(([, x]) => x === (res?.crc || "").toUpperCase());
        if (hit) log("SUCCESS", `Rumus checksum ECU = ${hit[0]} (cocok dengan nilai ECU 0x${res.crc}) ✓`);
        else log("WARN", `Tidak ada varian checksum file yang sama dengan nilai ECU 0x${res?.crc || "—"} → isi flash berbeda dari file ini, atau rutin menghitung area di luar file.`);
      }
      setProcStatus("CRC selesai ✓");
    } catch (e) { log("ERROR", `CRC check gagal: ${e.message || e}`); log("WARN", "Jika balasan kosong: pastikan tipe ECU sama dengan hasil READ INFO (alamat rutin checksum beda per tipe)."); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [checkEngine, firmware, expected, ecu, iface, ecuIdx, log, infoEcuIdx, askConfirm, onPhase]);

  const doWrite = useCallback(async () => {
    if (!firmware) { log("WARN", "Belum ada file BIN."); return; }
    // Cegah SEBELUM erase: part-code header file harus cocok dengan part ECU hasil READ INFO.
    const ecuPartNum = YAMAHA_PART_DB[(ecuInfo?.part || "").toUpperCase()] || "";
    const ecuPart = ecuPartNum.split("-")[0];
    const fileTag = firmware.partTag || "";
    const headerHasPart = ecuPart && (firmware.header || "").includes(ecuPart);
    if (ecuPart && fileTag && !headerHasPart && ecuPart.slice(0, 3) !== fileTag.slice(0, 3)) {
      const stop = await askConfirm(`FILE INI BUKAN UNTUK ECU TERPASANG\n\nECU terbaca: ${ecuPartNum}\nFile: ${firmware.name} (part-code ${fileTag})\n\nMenulis file motor lain akan merusak ECU, dan ECU ini TIDAK BISA direcovery.\n\nBatalkan WRITE? (disarankan: YA, batalkan)`);
      if (stop) { log("WARN", `WRITE dibatalkan — file ${fileTag} tidak cocok dengan ECU ${ecuPartNum}.`); return; }
      log("WARN", `Diteruskan paksa oleh user: file ${fileTag} ≠ ECU ${ecuPartNum}.`);
    }
    if (infoEcuIdx != null && infoEcuIdx !== ecuIdx) {
      const goType = await askConfirm(`TIPE ECU TIDAK SAMA\n\nDipilih sekarang: ${ecu.label}\nSaat READ INFO berhasil: ${YAMAHA_ECUS[infoEcuIdx].label}\n\nAlamat erase/download berbeda per tipe — tipe yang salah bisa merusak ECU.\n\nTetap lanjut? (tidak disarankan)`);
      if (!goType) { log("WARN", "WRITE dibatalkan — pilih tipe ECU yang sama dengan hasil READ INFO."); return; }
    }
    if (!sizeOk) {
      const go = await askConfirm(`Ukuran file ${firmware.bytes.length} bytes tidak sesuai tipe ECU terpilih (${expected.join(" / ")} bytes).\n\nLanjut tetap WRITE?`);
      if (!go) { log("WARN", "WRITE dibatalkan (ukuran file tidak sesuai)."); return; }
    }
    // Plain/dump = diketahui berbahaya (LEXY B3F: semua blok ACK tapi flash jadi acak & motor mati).
    // User boleh menerima risiko secara sadar, tapi wajib konfirmasi tegas dulu.
    if (firmware.entropy != null && firmware.entropy < 7.2) {
      const go2 = await askConfirm(`BAHAYA TINGGI — FILE INI PLAIN/DUMP (entropi ${firmware.entropy.toFixed(2)})\n\nECU Yamaha K-Line Denso MENDEKRIPSI data yang masuk. Mengirim file plain membuat isi flash jadi sampah walau semua blok dijawab OK — ini yang membuat ECU LEXY B3F tidak hidup.\n\nFile yang benar adalah versi TERENKRIPSI (entropi >7.5).\n\nAnda menerima risiko dan tetap mau WRITE?`);
      if (!go2) { log("WARN", "WRITE dibatalkan — gunakan file terenkripsi untuk motor ini."); return; }
      log("WARN", `RISIKO DITERIMA USER: menulis file plain (entropi ${firmware.entropy.toFixed(2)}) ke ${ecu.label}.`);
    }
    const ok = await askConfirm(`WRITE ${firmware.name}\n${firmware.bytes.length} bytes → ${ecu.label}\n\nFlash ECU akan dihapus & ditulis. Lanjut?`);
    if (!ok) { log("WARN", "WRITE dibatalkan user."); return; }
    setBusy(true); setProgress(0); setProcStatus("INITIAL…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("WRITE", `Flash session start · ${ecu.label} · ${firmware.bytes.length} bytes`);
      setWriteTs(Date.now());
      const res = await yamahaWrite(iface, ecuIdx, firmware.bytes, (m) => {
        if (m && m.progress != null && m.total) {
          const pct = Math.round((m.progress / m.total) * 100);
          setProgress(pct); setProcStatus(`WRITE… ${pct}%`);
        } else if (m && m.phase) {
          setProcStatus(PHASE_TXT[m.phase] || String(m.phase).toUpperCase());
          log("INFO", PHASE_TXT[m.phase] || `Fase: ${m.phase}`);
        } else if (m && m.msg) {
          log("INFO", String(m.msg));
        }
      });
      setProgress(100);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", `Selesai · ${res?.written ?? 0} bytes ditulis ke ECU.`);
      if (res?.blocksTotal) log("SUCCESS", `Blok ter-ACK: ${res.blocks}/${res.blocksTotal}`);
      if (res?.crc) log("INFO", `Rutin 31 02 → 0x${res.crc} (informasi, bukan checksum isi flash)`);
      setProcStatus("WRITE selesai — cek hasil di ECU");
      setFlashResult({ good: true, title: "FLASH GOOD", lines: [
        `${res?.written ?? firmware.bytes.length} bytes ditulis ke ECU · TransferExit diterima ECU ✓`,
        res?.crc ? `Rutin 31 02 → 0x${res.crc} (info, bukan checksum isi flash)` : "",
        res?.flagKept ? "Reprog flag DIBIARKAN 5555 — ECU masih bisa ditulis ulang." : "",
        res?.flagKept ? "Nyalakan motor dulu. Kalau normal, klik SELESAIKAN (FLAG NORMAL)." : "ECU sudah direset — nyalakan ulang IG dan tes motor.",
      ].filter(Boolean) });
    } catch (e) {
      const msg = String(e.message || e);
      log("ERROR", `WRITE gagal: ${msg}`);
      setProcStatus("GAGAL ✗");
      setFlashResult({ good: false, title: "FLASH BAD", lines: [
        msg,
        "ECU TIDAK direset dan reprog flag tidak diubah — ECU masih di mode programming dan bisa ditulis ulang.",
      ] });
    }
    finally { setBusy(false); }
  }, [firmware, sizeOk, expected, askConfirm, ecu, iface, ecuIdx, infoEcuIdx, ecuInfo, checkEngine, log]);

  // UJI PENENTU (read/write): tulis pola 0x00 penuh, lalu baca checksum.
  // Jika ECU menyimpan data mentah → Σ16BE harus 0x0000. Jika ECU mentransformasi/mendekripsi
  // data masuk → hasilnya angka lain yang tetap. Ini memisahkan dua hipotesis secara pasti.
  const doPatternTest = useCallback(async (fill) => {
    if (ecuIdx !== 3) {
      log("ERROR", `UJI POLA hanya untuk MC9S12XS256 · 256KB · K-Line. Tipe terpilih sekarang: ${ecu.label}. Pilih tipe ECU yang benar dulu (atau jalankan READ INFO / AUTO DETECT).`);
      return;
    }
    const len = 245760;
    const fb = fill & 0xFF;
    const hexfill = fb.toString(16).padStart(2, "0").toUpperCase();
    // Σ16BE pola konstan atas rentang verify 0x3BC00 (122368 word)
    const expSum = ((fb * 256 + fb) * 122368) & 0xFFFF;
    const go = await askConfirm(`UJI POLA 0x${hexfill} (${len} byte)\n\nIni MENULIS pola 0x${hexfill} ke flash ECU, bukan file motor.\n\nHanya lakukan pada ECU yang MEMANG sudah tidak hidup. Lanjut?`);
    if (!go) { log("WARN", "Uji pola dibatalkan."); return; }
    setBusy(true); setProgress(0); setProcStatus("UJI POLA…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("WRITE", `UJI POLA 0x${hexfill} · ${ecu.label} · ${len} byte (Σ16BE pola atas rentang verify = 0x${expSum.toString(16).padStart(4, "0").toUpperCase()})`);
      const buf = new Uint8Array(len).fill(fb);
      const res = await yamahaWrite(iface, ecuIdx, buf, (m) => {
        if (m && m.progress != null && m.total) {
          const pct = Math.round((m.progress / m.total) * 100);
          setProgress(pct); setProcStatus(`UJI POLA… ${pct}%`);
        } else if (m && m.msg) log("INFO", String(m.msg));
      });
      setProgress(100);
      const crc = (res?.crc || "").toUpperCase();
      const expHex = expSum.toString(16).padStart(4, "0").toUpperCase();
      if (crc) {
        log(crc === expHex ? "SUCCESS" : "WARN", `HASIL UJI POLA 0x${hexfill}: rutin 31 02 → 0x${crc} (pola mentah seharusnya 0x${expHex})`);
        log("INFO", crc === expHex
          ? "COCOK = ECU menyimpan data MENTAH pada uji ini."
          : `BEDA = isi flash bukan pola yang kita kirim. Kirim angka ini ke saya untuk dibandingkan dengan uji pola lain.`);
      } else log("WARN", `Engine tidak mengembalikan nilai 31 02. Balasan engine: ${JSON.stringify(res)}`);
      setProcStatus("UJI POLA selesai");
    } catch (e) {
      const msg = String(e.message || e);
      log("ERROR", `Uji pola berhenti: ${msg}`);
      const m = /ECU (0x)?([0-9A-Fa-f]{4})/.exec(msg);
      if (m) log("INFO", `Nilai 31 02 saat uji pola 0x${hexfill} = 0x${m[2].toUpperCase()} (pola mentah seharusnya 0x${expSum.toString(16).padStart(4, "0").toUpperCase()}) — ini hasil yang saya butuhkan.`);
      setProcStatus("UJI POLA selesai (lihat log)");
    }
    finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askConfirm, checkEngine, ecu, iface, ecuIdx, expected, log]);

  const doScanBus = useCallback(async () => {
    setBusy(true); setProcStatus("SCAN BUS…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("INFO", "SCAN BUS (read-only): 500k/250k/125k · 11-bit 7E0/7E1/7E2/7DF · 29-bit 18DA10F1/18DB33F1.");
      const res = await yamahaScanBus(iface, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", `ECU MASIH HIDUP di: ${res.found}`);
      setProcStatus("SCAN BUS selesai ✓");
      setFlashResult({ good: true, title: "ECU MASIH MENJAWAB", lines: [res.found, "Kirim baris ini ke saya supaya jalur recovery diarahkan ke bus tersebut."] });
    } catch (e) {
      const msg = String(e.message || e);
      log("ERROR", `SCAN BUS: ${msg}`);
      setProcStatus("SUNYI ✗");
      setFlashResult({ good: false, title: "ECU TIDAK MENJAWAB", lines: [msg] });
    }
    finally { setBusy(false); }
  }, [checkEngine, iface, onPhase, log]);

  const doRecover = useCallback(async () => {
    setBusy(true); setProcStatus("RECOVERY…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("INFO", "RECOVERY: memaksa mode programming (tanpa READ INFO) — IG harus ON.");
      const res = await yamahaRecover(iface, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", `ECU menjawab (${res.hit}) · reprog flag di-set ${res.flag}.`);
      setProcStatus("RECOVERY siap ✓");
      setFlashResult({ good: true, title: "RECOVERY SIAP", lines: [
        `ECU menjawab: ${res.hit}`,
        "Reprog flag sudah 5555 — ECU bisa ditulis ulang.",
        "Buka file ORI lalu klik WRITE. JANGAN klik SELESAIKAN dulu.",
      ] });
    } catch (e) {
      const msg = String(e.message || e);
      log("ERROR", `RECOVERY gagal: ${msg}`);
      setProcStatus("GAGAL ✗");
      setFlashResult({ good: false, title: "RECOVERY GAGAL", lines: [msg] });
    }
    finally { setBusy(false); }
  }, [checkEngine, iface, onPhase, log]);

  const finalizeReady = !!(ecuInfo && (ecuInfo.part || "").length > 0 && infoTs > writeTs);

  const doFinalize = useCallback(async () => {
    if (!finalizeReady) {
      log("WARN", "FLAG NORMAL dikunci: jalankan READ INFO setelah WRITE dan pastikan ECU menjawab (hardware code terbaca) + motor hidup normal.");
      setFlashResult({ good: false, title: "BELUM BOLEH FINALISASI", lines: [
        "Jalankan READ INFO setelah WRITE dan pastikan hardware code terbaca.",
        "Pastikan juga motor benar-benar hidup normal.",
        "Selama flag masih 5555 ECU tetap bisa ditulis ulang — ini pengaman Anda.",
      ] });
      return;
    }
    const ok = await askConfirm("SELESAIKAN (FLAG NORMAL)\n\nReprog flag ECU dikembalikan ke normal (0000) lalu ECU direset.\nSetelah ini ECU tidak lagi di mode programming.\n\nMotor sudah benar-benar hidup normal? Lanjut?");
    if (!ok) return;
    setBusy(true); setProcStatus("FINALIZE…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      const res = await yamahaFinalize(iface, ecuIdx, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", "Reprog flag dikembalikan ke normal · ECU direset.");
      setProcStatus("FINALIZE selesai ✓");
      setFlashResult({ good: true, title: "SELESAI", lines: ["Reprog flag sudah normal (0000) dan ECU direset."] });
    } catch (e) { log("ERROR", `Finalize gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [askConfirm, checkEngine, iface, ecuIdx, onPhase, log, finalizeReady]);

  // Peta verifikasi read-only: bandingkan Σ16BE file per sub-rentang dengan hasil rutin 31 02 ECU,
  // supaya ketahuan DI MANA isi flash berbeda (sektor gagal tulis vs beda menyeluruh).
  const doVerifyMap = useCallback(async () => {
    if (!firmware) { log("WARN", "Muat file dulu — peta verifikasi membandingkan ECU dengan file."); return; }
    setBusy(true); setProcStatus("VERIFY MAP…"); setProgress(0);
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      const res = await yamahaVerifyMap(iface, ecuIdx, mapChunks, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      const b = firmware.bytes;
      let bad = 0, ok = 0;
      (res.map || []).forEach((m) => {
        const seg = b.subarray(m.off, m.off + m.len);
        let be = 0;
        for (let i = 0; i + 1 < seg.length; i += 2) be += (seg[i] << 8) | seg[i + 1];
        const want = hex4(be);
        if (!m.crc) { log("WARN", `  0x${m.off.toString(16).toUpperCase()} → ECU tidak menjawab`); return; }
        if (m.crc === want) { ok++; log("SUCCESS", `  0x${m.off.toString(16).toUpperCase()} cocok (0x${m.crc})`); }
        else { bad++; log("ERROR", `  0x${m.off.toString(16).toUpperCase()} BEDA — ECU 0x${m.crc} vs file 0x${want}`); }
      });
      log(bad === 0 ? "SUCCESS" : "WARN", `Peta verifikasi: ${ok} cocok · ${bad} beda`);
      setProcStatus(bad === 0 ? "VERIFY MAP cocok ✓" : `VERIFY MAP: ${bad} potongan beda`);
    } catch (e) { log("ERROR", `Verify map gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkEngine, iface, ecuIdx, onPhase, log, firmware, mapChunks]);

  // READ BACK (read-only): baca isi flash sebenarnya lalu bandingkan byte-per-byte dengan file.
  const doReadBack = useCallback(async () => {
    if (!firmware) { log("WARN", "Muat file dulu — read-back dibandingkan dengan file."); return; }
    setBusy(true); setProcStatus("READ BACK…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      const res = await yamahaReadBack(iface, ecuIdx, 0, 256, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      if (!res.len) {
        log("WARN", "ECU tidak melayani permintaan baca memori (semua varian ditolak).");
        (res.probes || "").split("|").filter(Boolean).forEach((p) => log("INFO", `  ${p}`));
        setProcStatus("READ BACK: tidak didukung");
        return;
      }
      const ecuBytes = new Uint8Array(res.len);
      for (let i = 0; i < res.len; i++) ecuBytes[i] = parseInt(res.data.substr(i * 2, 2), 16);
      const hx = (a) => Array.from(a).map((v) => v.toString(16).padStart(2, "0")).join(" ");
      log("INFO", `ECU  @0x${res.addr} (${res.method}): ${hx(ecuBytes.subarray(0, 32))}`);
      log("INFO", `FILE @0x000000: ${hx(firmware.bytes.subarray(0, 32))}`);
      const f = firmware.bytes;
      let same = 0;
      for (let i = 0; i < ecuBytes.length && i < f.length; i++) if (ecuBytes[i] === f[i]) same++;
      log(same === ecuBytes.length ? "SUCCESS" : "WARN", `Cocok ${same}/${ecuBytes.length} byte pada offset 0.`);
      // deteksi pergeseran: cari 16 byte pertama ECU di dalam file
      const needle = ecuBytes.subarray(0, 16);
      let found = -1;
      for (let p = 0; p + 16 <= f.length && found < 0; p++) {
        let ok = true;
        for (let i = 0; i < 16; i++) if (f[p + i] !== needle[i]) { ok = false; break; }
        if (ok) found = p;
      }
      if (found >= 0) log(found === 0 ? "SUCCESS" : "WARN", `16 byte pertama ECU ditemukan di file pada offset 0x${found.toString(16).toUpperCase()}${found ? " → ada PERGESERAN alamat" : ""}`);
      else {
        // cek byte-swap
        let sw = 0;
        for (let i = 0; i + 1 < 16; i += 2) if (ecuBytes[i] === f[i + 1] && ecuBytes[i + 1] === f[i]) sw++;
        log("WARN", sw >= 7 ? "Pola BYTE-SWAP terdeteksi (pasangan byte tertukar)." : "Isi ECU tidak ditemukan di file — bukan pergeseran sederhana maupun byte-swap.");
      }
      setProcStatus(same === ecuBytes.length ? "READ BACK cocok ✓" : "READ BACK: isi beda");
    } catch (e) { log("ERROR", `Read back gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [checkEngine, iface, ecuIdx, onPhase, log, firmware]);

  // Flag tersangkut (mis. 5AA5): kembalikan HANYA reprog flag ke 0000, tanpa menulis serial/tanggal.
  const flagStuck = !!(ecuInfo?.reflag && !/^0{4}$/.test(ecuInfo.reflag));
  const doResetFlag = useCallback(async () => {
    const ok = await askConfirm("RESET FLAG\n\nMengembalikan reprog flag ECU ke 0000 lalu reset ECU.\nSerial & tanggal reprog TIDAK disentuh.\n\nDipakai kalau flag tersangkut (mis. 5AA5) dan ECU menolak erase/download.\nFlash ECU tidak dihapus. Lanjut?");
    if (!ok) return;
    setBusy(true); setProcStatus("RESET FLAG…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      const res = await yamahaResetFlag(iface, ecuIdx, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", "Reprog flag di-reset ke 0000 · ECU direset. Jalankan READ INFO untuk konfirmasi.");
      setProcStatus("RESET FLAG selesai ✓");
    } catch (e) { log("ERROR", `Reset flag gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [askConfirm, checkEngine, iface, ecuIdx, onPhase, log]);

  const doLive = useCallback(async () => {
    setBusy(true); setProcStatus("LIVE DATA…"); setProgress(0); setLiveFrames([]);
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("INFO", `Live data (21 01) · ${ecu.label} — ikuti perintah IG OFF / IG ON.`);
      const pid = parseInt(livePid, 16) || 1;
      const res = await yamahaLive(iface, ecuIdx, 60, pid, (m) => {
        if (m && m.live) setLiveFrames((f) => [`${now()}  ${m.live}`, ...f].slice(0, 60));
        else onPhase(m);
      });
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", `Live data selesai · ${res.frames} frame diterima · PID 21 ${res.pid} · terakhir: ${res.last || "—"}`);
      setProcStatus("LIVE selesai ✓");
    } catch (e) { log("ERROR", `Live data gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [checkEngine, iface, ecuIdx, ecu, log, onPhase, livePid]);

  const doScanPids = useCallback(async () => {
    setBusy(true); setProcStatus("SCAN PID…"); setLiveFrames([]);
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("INFO", "Scan identifier 21 00–FF dan 22 00–FF (hanya membaca) — ikuti perintah IG OFF / IG ON. Bisa beberapa menit.");
      const res = await yamahaScanPids(iface, ecuIdx, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      const pids = String(res.pids || "").split("|").filter(Boolean);
      pids.forEach((p) => { const [k, v] = p.split(":"); log("SUCCESS", `${k.slice(0, 2)} ${k.slice(2)} → ${v}`); });
      if (pids.length) { setLivePid(pids[0].split(":")[0].slice(2)); log("INFO", `PID live diset otomatis ke ${pids[0].split(":")[0]}`); }
      else log("WARN", "Tidak ada identifier 21/22 xx yang dijawab ECU ini — gunakan YMETER (15625) untuk live data motor ini.");
      setProcStatus("SCAN PID selesai ✓");
    } catch (e) { log("ERROR", `Scan PID gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [checkEngine, iface, ecuIdx, log, onPhase]);

  const doYmeter = useCallback(async () => {
    setBusy(true); setProcStatus("YMETER 15625…"); setLiveFrames([]); setSensorItems({});
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("INFO", "YMETER / YDT via J2534 @15625 — lakukan IG ON → IG OFF → IG ON, biarkan IG ON.");
      const res = await yamahaYmeter(iface, 40, (m) => {
        if (m && m.live) {
          setLiveFrames((f) => [`${now()}  ${m.live}`, ...f].slice(0, 60));
          const mm = /^(D[0-9A-F]{2}) = 0x([0-9A-F]{2})/i.exec(m.live);
          if (mm) setSensorItems((s) => ({ ...s, [mm[1].toUpperCase()]: mm[2].toUpperCase() }));
        }
        else onPhase(m);
      });
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", `YMETER connected · frame hello ${res.hello} · ${res.count} item terbaca`);
      const fin = {};
      String(res.items || "").split("|").filter(Boolean).forEach((it) => { const [k, v] = it.split(":"); fin[k.toUpperCase()] = v.toUpperCase(); log("INFO", `${k} = 0x${v}`); });
      if (Object.keys(fin).length) setSensorItems(fin);
      setProcStatus("YMETER selesai ✓");
    } catch (e) { log("ERROR", `YMETER gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [checkEngine, iface, log, onPhase]);

  const doClearDtc = useCallback(async () => {
    const ok = await askConfirm("Hapus DTC (14 FF FF FF) di ECU Yamaha?\n\nIkuti perintah IG OFF / IG ON saat diminta.");
    if (!ok) return;
    setBusy(true); setProcStatus("CLEAR DTC…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      const res = await yamahaClearDtc(iface, ecuIdx, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("SUCCESS", `DTC dihapus · balasan ECU ${res.resp}`);
      setProcStatus("CLEAR DTC selesai ✓");
    } catch (e) { log("ERROR", `Clear DTC gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [askConfirm, checkEngine, iface, ecuIdx, log, onPhase]);

  const doProbeRead = useCallback(async () => {
    const ok = await askConfirm("PROBE READ (EKSPERIMEN)\n\nTool Yamaha asli TIDAK punya fitur baca flash, jadi ini mencoba beberapa varian perintah baca (service 23/35) lalu melaporkan balasan mentah ECU.\n\nTidak ada erase/write — aman, hanya membaca. Lanjut?");
    if (!ok) return;
    setBusy(true); setProcStatus("PROBE READ…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      const res = await yamahaProbeRead(iface, ecuIdx, onPhase);
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      log("INFO", `Security Access: ${res.security ? "OK" : "GAGAL"} · alamat uji 0x${res.addr}`);
      String(res.probes || "").split("|").filter(Boolean).forEach((p) => log("INFO", `probe ${p}`));
      log(res.hit ? "SUCCESS" : "WARN", res.hit
        ? `Ada varian yang dijawab ECU: ${res.hit} — kirim log ini supaya saya buat rutin READ BIN penuh.`
        : "Tidak ada varian baca yang dijawab ECU (kemungkinan ECU ini memang tidak mengizinkan read).");
      setProcStatus("PROBE selesai ✓");
    } catch (e) { log("ERROR", `Probe read gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [askConfirm, checkEngine, iface, ecuIdx, log, onPhase]);

  const doReadCan = useCallback(async () => {
    const size = firmware ? firmware.bytes.length : expected[0];
    const ok = await askConfirm(`READ / DUMP FLASH (EKSPERIMEN)\n\nTipe ECU: ${ecu.label}\nUkuran target: ${size} bytes\n\nTool Yamaha asli TIDAK punya rutin baca flash — ini mencoba service 23 / RequestUpload 35 pada alamat yang dipakai rutin checksum. Hanya membaca, tidak ada erase/write.\n\nLanjut?`);
    if (!ok) return;
    setBusy(true); setProgress(0); setProcStatus("READ FLASH…");
    try {
      const r = await checkEngine();
      if (!r.online) throw new Error("Yamaha Engine belum jalan");
      log("INFO", `READ flash CAN · ${ecu.label} · target ${size} bytes…`);
      const res = await yamahaReadCan(iface, ecuIdx, size, (m) => {
        if (m && m.progress != null && m.total) {
          const pct = Math.round((m.progress / m.total) * 100);
          setProgress(pct); setProcStatus(`READ… ${pct}%`);
        } else onPhase(m);
      });
      if (!res || res.ok === false) throw new Error(res?.error || "engine menolak permintaan");
      const hex = String(res.data || "");
      const bytes = new Uint8Array(hex.length / 2);
      for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
      setProgress(100);
      log("SUCCESS", `Terbaca ${bytes.length} byte dari 0x${res.addr} (metode ${res.method}).`);
      if (res.expected && bytes.length < res.expected)
        log("WARN", `Dump TIDAK lengkap (${bytes.length}/${res.expected} byte) — jangan dipakai sebagai file flash.`);
      const blob = new Blob([bytes], { type: "application/octet-stream" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `yamaha_read_${res.addr}_${bytes.length}.bin`;
      a.click();
      URL.revokeObjectURL(a.href);
      log("INFO", "File .bin disimpan. Verifikasi dengan CRC CHECK — checksum ECU harus sama dengan checksum dump.");
      setProcStatus("READ selesai ✓");
    } catch (e) { log("ERROR", `READ flash gagal: ${e.message || e}`); setProcStatus("GAGAL ✗"); }
    finally { setBusy(false); }
  }, [askConfirm, checkEngine, firmware, expected, ecu, iface, ecuIdx, log, onPhase]);

  const copyLog = () => { try { navigator.clipboard.writeText(logs.map((l) => l.text).join("\n")); } catch (_) {} };

  // Potong dump penuh jadi payload siap-flash (mis. 256 KB XS256 -> 245.760 B pertama).
  // Ekor yang dibuang = bootloader + vector table, yang memang tidak pernah ditulis tool asli.
  const trimInfo = firmware ? FULL_DUMP_SIZES[firmware.bytes.length] : null;
  const trimPayload = () => {
    if (!trimInfo) return;
    const cut = firmware.bytes.slice(0, trimInfo.payload);
    const blob = new Blob([cut], { type: "application/octet-stream" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = firmware.name.replace(/\.bin$/i, "") + `_payload_${trimInfo.payload}.bin`;
    a.click();
    URL.revokeObjectURL(a.href);
    log("SUCCESS", `Payload dipotong: ${trimInfo.payload} byte pertama disimpan (Σ16BE 0x${sumVariants(cut).sum16BE}). Muat file hasil potong ini untuk WRITE.`);
  };
  const exportMoto = useCallback(async () => {
    if (!firmware) return;
    try {
      const enc = await encryptMoto(firmware.bytes);
      const blob = new Blob([enc], { type: "application/octet-stream" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = firmware.name.replace(/\.(bin|moto)$/i, "") + ".moto";
      a.click();
      URL.revokeObjectURL(a.href);
      log("SUCCESS", `BIN → .moto: ${firmware.bytes.length} byte BIN dienkripsi → ${enc.length} byte .moto (2×BIN+32).`);
    } catch (e) { log("ERROR", `Gagal membuat .moto: ${e.message || e}`); }
  }, [firmware, log]);
  const handleClose = () => (onClose ? onClose() : navigate("/"));

  const hwHits = useMemo(() => {
    const q = hwQuery.trim().toUpperCase();
    const all = Object.entries(YAMAHA_PART_DB);
    if (!q) return all.slice(0, 40);
    return all.filter(([k, v]) => k.includes(q) || v.toUpperCase().includes(q)).slice(0, 60);
  }, [hwQuery]);

  const ifaceLabel = YAMAHA_IFACES.find((x) => x.id === iface)?.label || iface;

  const exportPartDb = async () => {
    try {
      const r = await api.get("/downloads/Yamaha-PartNumber-DB.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "Yamaha-PartNumber-DB.xlsx"; a.click();
      URL.revokeObjectURL(url);
      log("SUCCESS", "Database part number diekspor ke Yamaha-PartNumber-DB.xlsx");
    } catch (e) {
      log("ERROR", `Export gagal: ${e?.response?.status === 401 ? "login dulu di aplikasi TRMD" : e.message}`);
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#05080e] text-slate-200" data-testid="yamaha-flashtools-page" data-i18n-lock="en">
      {confirmState && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[min(92vw,480px)]" data-testid="yamaha-confirm">
          <div className="rounded-xl border border-sky-500/40 bg-[#0a1120]/95 backdrop-blur-md shadow-2xl p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-amber-400 shrink-0 mt-0.5" size={24} />
              <div className="text-[15px] leading-relaxed text-slate-100 whitespace-pre-line flex-1">{confirmState.message}</div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button data-testid="yamaha-confirm-cancel" onClick={() => resolveConfirm(false)} className="px-5 py-2 rounded-lg text-[14px] font-semibold text-slate-200 bg-slate-700/60 hover:bg-slate-700">Batal</button>
              <button data-testid="yamaha-confirm-ok" onClick={() => resolveConfirm(true)} className="px-5 py-2 rounded-lg text-[14px] font-bold text-white bg-sky-600 hover:bg-sky-500">OK</button>
            </div>
          </div>
        </div>
      )}

      {flashResult && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] w-[min(92vw,520px)]" data-testid="yamaha-flash-result">
          <div className={`rounded-xl border-2 ${flashResult.good ? "border-emerald-500/70 bg-[#06160f]/95" : "border-red-500/70 bg-[#190a0a]/95"} backdrop-blur-md shadow-2xl p-6`}>
            <div className="flex items-center gap-3">
              {flashResult.good ? <CheckCircle2 className="text-emerald-400" size={34} /> : <AlertTriangle className="text-red-400" size={34} />}
              <span data-testid="yamaha-flash-result-title" className={`text-[26px] font-black tracking-wide ${flashResult.good ? "text-emerald-300" : "text-red-300"}`}>{flashResult.title}</span>
            </div>
            <div className="mt-3 space-y-1.5 text-[14px] leading-relaxed text-slate-200">
              {flashResult.lines.map((l, i) => <div key={i}>{l}</div>)}
            </div>
            <div className="flex justify-end mt-5">
              <button data-testid="yamaha-flash-result-ok" onClick={() => setFlashResult(null)}
                className={`px-6 py-2 rounded-lg text-[14px] font-bold text-white ${flashResult.good ? "bg-emerald-600 hover:bg-emerald-500" : "bg-red-600 hover:bg-red-500"}`}>OK</button>
            </div>
          </div>
        </div>
      )}

      <div className="relative shrink-0 overflow-hidden bg-[#05080e] h-[80px]" data-testid="yamaha-trmd-banner">
        <img src="/trmd-banner.webp" alt="" className="h-full w-full object-cover object-left select-none pointer-events-none opacity-60" draggable={false} style={{ filter: "hue-rotate(185deg) saturate(1.3)" }} />
        <div className="absolute inset-0 flex items-center pl-6">
          <span className="text-white font-black text-[24px] tracking-wide" data-testid="yamaha-title">YAMAHA <span className="text-sky-400">FLASH TOOLS</span></span>
        </div>
        <img src="/trmd-logo.webp" alt="TRMD" data-testid="yamaha-trmd-logo" draggable={false} className="absolute right-6 top-1/2 -translate-y-1/2 h-[60px] w-auto object-contain select-none pointer-events-none" />
      </div>

      <div className="w-full flex-1 min-h-0 flex flex-col p-3 gap-3">
        <div className="flex-1 min-h-0 flex flex-col gap-3">

          {tab === "remap" && (
            <>
              <div className="grid grid-cols-3 [.ft-narrow_&]:grid-cols-1 gap-4 shrink-0">
                <Panel className="col-span-2 [.ft-narrow_&]:col-span-1">
                  <PanelHead icon={<Cpu size={24} />} title="INFO ECU YAMAHA" />
                  <div className="grid grid-cols-2 max-[520px]:grid-cols-1 gap-x-6 gap-y-3 p-4">
                    <Row label="Part Number" value={YAMAHA_PART_DB[(ecuInfo?.part || "").toUpperCase()] || ecuInfo?.label || ""} testid="yamaha-info-part" />
                    <Row label="Software Version" value={(ecuInfo?.ver || "").toUpperCase()} testid="yamaha-info-swver" />
                    <Row label="Model Code" value={ecuInfo?.soft || ""} testid="yamaha-info-model" />
                    <Row label="Reprogram Flag" value={ecuInfo?.reflag || ""} testid="yamaha-info-reflag" />
                    <Row label="Version Number" value={(ecuInfo?.vendor || "").toUpperCase()} testid="yamaha-info-vernum" />
                    <Row label="Flash Size" value={ecuInfo ? flashCapacity : ""} testid="yamaha-info-size" />
                    <Row label="Software Id" value={(ecuInfo?.part || "").toUpperCase()} testid="yamaha-info-ecmid" />
                    <Row label="VIN" value={ecuInfo?.vinpid || ""} testid="yamaha-info-vin" />
                  </div>
                </Panel>

                <Panel>
                  <PanelHead icon={<FileText size={24} />} title="INFO FILE" right={
                    <button data-testid="yamaha-close-btn" onClick={handleClose} disabled={busy} title="Tutup"
                      style={{ clipPath: "polygon(24px 0, 100% 0, 100% 100%, 0 100%)" }}
                      className={`h-full pl-10 pr-5 text-white flex items-center justify-center ${busy ? "bg-black/30 opacity-40 cursor-not-allowed" : "bg-gradient-to-b from-[#1d7fe0] to-[#1159a8] hover:from-[#2b92f3] hover:to-[#1668c0]"}`}>
                      <X size={22} strokeWidth={2.75} />
                    </button>
                  } />
                  <div className="flex flex-col gap-3 p-4">
                    <div className="flex items-center gap-3">
                      <span className="w-36 shrink-0 text-[16px] font-medium text-white">Protocol</span>
                      <select data-testid="yamaha-proto-select" value={proto} onChange={(e) => changeProto(e.target.value)} disabled={busy}
                        className="flex-1 min-w-0 h-8 px-2 rounded-[3px] border border-[#6b7280] bg-[#101722] text-slate-100 text-[13px] font-mono outline-none focus:border-sky-500 disabled:opacity-50">
                        {YAMAHA_PROTOS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
                      </select>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-36 shrink-0 text-[16px] font-medium text-white">ECU Type</span>
                      <select data-testid="yamaha-ecu-select" value={ecuIdx} onChange={(e) => setEcuIdx(Number(e.target.value))} disabled={busy}
                        className="flex-1 min-w-0 h-8 px-2 rounded-[3px] border border-[#6b7280] bg-[#101722] text-slate-100 text-[13px] font-mono outline-none focus:border-sky-500 disabled:opacity-50">
                        {protoList.map((i) => <option key={i} value={i}>{YAMAHA_ECUS[i].label}</option>)}
                      </select>
                      <button data-testid="yamaha-auto-ecu" onClick={doAutoEcu} disabled={busy} title="Deteksi tipe ECU dari ECU yang terhubung (read-only)"
                        className="h-8 px-2 shrink-0 rounded-[3px] border border-emerald-500/60 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 text-[12px] font-bold disabled:opacity-40">
                        AUTO
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-36 shrink-0 text-[16px] font-medium text-white">Interface</span>
                      <select data-testid="yamaha-iface-select" value={iface} onChange={(e) => setIface(e.target.value)} disabled={busy}
                        className="flex-1 min-w-0 h-8 px-2 rounded-[3px] border border-[#6b7280] bg-[#101722] text-slate-100 text-[13px] font-mono outline-none focus:border-sky-500 disabled:opacity-50">
                        {YAMAHA_IFACES.map((x) => {
                          const d = devices.find((y) => y.kind === x.id);
                          const winOnly = IS_ANDROID && (x.id === "sm3" || x.id === "sm3wifi" || x.id === "tactrix");
                          return <option key={x.id} value={x.id} disabled={winOnly}>{x.label}{winOnly ? " (Windows saja)" : devices.length ? (d ? (d.online ? " ✓ tercolok" : d.present ? " · tidak merespons" : " · DLL hilang") : " · tidak terdaftar") : ""}</option>;
                        })}
                      </select>
                      <button data-testid="yamaha-detect-devices" onClick={detectDevices} disabled={busy}
                        className="h-8 px-2 shrink-0 rounded-[3px] border border-sky-500/60 bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 text-[12px] font-bold disabled:opacity-40">
                        DETEKSI
                      </button>
                    </div>
                  </div>
                </Panel>
              </div>

              <Panel className="flex-1 min-h-0 flex flex-col">
                <PanelHead icon={<ClipboardList size={24} />} title="TASK" />
                <div className="flex flex-col flex-1 min-h-0 p-4 gap-3">
                  <input ref={fileRef} type="file" accept=".bin,.moto" className="hidden" data-testid="yamaha-file-input"
                    onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleFile(f); if (e.target) e.target.value = ""; }} />
                  <div className="grid grid-cols-3 [.ft-narrow_&]:grid-cols-1 flex-1 min-h-0 [.ft-narrow_&]:min-h-[380px] gap-4">
                    <div className="col-span-2 [.ft-narrow_&]:col-span-1 flex flex-col min-h-0 gap-2">
                      <div className="flex items-center gap-2 shrink-0">
                        <button data-testid="yamaha-open-bin" onClick={() => fileRef.current?.click()} disabled={busy}
                          className="flex-1 min-w-0 h-9 rounded-[3px] border border-[#6b7280] bg-[#101722] hover:border-sky-500/60 px-4 flex items-center text-left text-[14px] font-mono text-slate-100 truncate">
                          <span data-testid="yamaha-file-name" className="truncate">{firmware ? `${firmware.name}  :  ${firmware.bytes.length} bytes` : "No file loaded — click to OPEN FILE"}</span>
                        </button>
                        <button data-testid="yamaha-open-bin-btn" onClick={() => fileRef.current?.click()} disabled={busy}
                          className="h-9 px-4 text-[14px] shrink-0 rounded-[3px] bg-gradient-to-b from-[#1d7fe0] to-[#1159a8] hover:from-[#2b92f3] hover:to-[#1668c0] text-white font-bold uppercase inline-flex items-center gap-2 disabled:opacity-40">
                          <Upload size={16} /> Open File
                        </button>
                        {firmware && (
                          <button data-testid="yamaha-export-moto" onClick={exportMoto} disabled={busy} title="Enkripsi BIN yang dimuat menjadi file .moto"
                            className="h-9 px-3 text-[13px] shrink-0 rounded-[3px] border border-emerald-500/60 bg-emerald-900/30 hover:bg-emerald-800/40 text-emerald-200 font-semibold uppercase inline-flex items-center gap-2 disabled:opacity-40">
                            <Download size={16} /> BIN → .moto
                          </button>
                        )}
                        {trimInfo && (
                          <button data-testid="yamaha-trim-payload" onClick={trimPayload} disabled={busy} title={`Simpan ${trimInfo.payload} byte pertama sebagai file siap-flash`}
                            className="h-9 px-3 text-[13px] shrink-0 rounded-[3px] border border-amber-500/60 bg-amber-900/30 hover:bg-amber-800/40 text-amber-200 font-semibold uppercase inline-flex items-center gap-2 disabled:opacity-40">
                            <HardDriveDownload size={16} /> Potong Payload
                          </button>
                        )}
                        <button data-testid="yamaha-copy-log" onClick={copyLog} title="Copy log" className="h-9 w-9 shrink-0 rounded-[3px] border border-[#6b7280] bg-[#111a26] hover:bg-[#1b2735] text-white inline-flex items-center justify-center"><Copy size={18} /></button>                        <button data-testid="yamaha-clear-log" onClick={() => setLogs([])} title="Hapus log" className="h-9 w-9 shrink-0 rounded-[3px] bg-sky-700 hover:bg-sky-600 text-white inline-flex items-center justify-center"><Trash2 size={18} /></button>
                      </div>
                      <div ref={logBoxRef} data-testid="yamaha-console" data-no-i18n
                        className="flex-1 min-h-[120px] w-full overflow-auto whitespace-pre-wrap break-words rounded-sm border border-[#6b7280] bg-[#0a0f17] text-white text-[13px] font-mono p-3 select-text">
                        {logs.length ? logs.map((l, i) => <div key={i} data-level={l.level}>{l.text}</div>) : <div>Yamaha Flash Tools — ready. Jalankan TRMD Yamaha Engine, pilih interface & tipe ECU.</div>}
                      </div>
                    </div>
                    <div className="flex flex-col h-full min-h-0 gap-2">
                      <div className="grid grid-cols-2 gap-2 shrink-0">
                        <button data-testid="yamaha-read-info" onClick={doInfo} disabled={busy}
                          className="h-[44px] px-2 rounded border border-sky-600 bg-sky-600 hover:bg-sky-700 text-white font-bold inline-flex items-center justify-center gap-2 text-[14px] disabled:opacity-40">
                          <Link2 size={18} /> READ INFO
                        </button>
                        <button data-testid="yamaha-crc-check" onClick={doCrc} disabled={busy}
                          className="h-[44px] px-2 rounded border border-sky-500/50 bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 font-bold inline-flex items-center justify-center gap-2 text-[14px] disabled:opacity-40">
                          <Hash size={18} /> CRC CHECK
                        </button>
                        <div className="flex items-center gap-1">
                          <button data-testid="yamaha-verify-map" onClick={doVerifyMap} disabled={busy || !firmware}
                            title="Read-only: bandingkan checksum ECU per sub-rentang dengan file"
                            className="flex-1 h-[44px] px-2 rounded border border-indigo-500/50 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-300 font-bold inline-flex items-center justify-center gap-2 text-[13px] disabled:opacity-40">
                            <ScanSearch size={18} /> Verify Map
                          </button>
                          <select data-testid="yamaha-verify-map-chunks" value={mapChunks} disabled={busy}
                            onChange={(e) => setMapChunks(Number(e.target.value))}
                            title="Jumlah potongan untuk uji konsistensi rutin checksum ECU"
                            className="h-[44px] rounded border border-indigo-500/50 bg-black/40 text-indigo-300 text-[12px] px-1">
                            <option value={4}>4</option>
                            <option value={8}>8</option>
                            <option value={16}>16</option>
                            <option value={32}>32</option>
                            <option value={64}>64</option>
                            <option value={128}>128</option>
                          </select>
                        </div>
                        <button data-testid="yamaha-pattern-test" onClick={() => doPatternTest(0x00)} disabled={busy}
                          title="Tulis pola 0x00 lalu baca checksum"
                          className="h-[44px] px-2 rounded border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-bold inline-flex items-center justify-center gap-2 text-[13px] disabled:opacity-40">
                          <ScanSearch size={18} /> Pola 00
                        </button>
                        <button data-testid="yamaha-pattern-test-ff" onClick={() => doPatternTest(0xFF)} disabled={busy}
                          title="Tulis pola 0xFF lalu baca checksum — pembanding untuk pola 00"
                          className="h-[44px] px-2 rounded border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-bold inline-flex items-center justify-center gap-2 text-[13px] disabled:opacity-40">
                          <ScanSearch size={18} /> Pola FF
                        </button>
                        <button data-testid="yamaha-read-back" onClick={doReadBack} disabled={busy || !firmware}
                          title="Read-only: baca 256 byte isi flash ECU dan bandingkan byte-per-byte dengan file"
                          className="h-[44px] px-2 rounded border border-cyan-500/50 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 font-bold inline-flex items-center justify-center gap-2 text-[13px] disabled:opacity-40">
                          <ScanSearch size={18} /> Read Back
                        </button>
                        <button data-testid="yamaha-reset-flag" onClick={doResetFlag} disabled={busy || !flagStuck}
                          title={flagStuck ? `Flag ECU ${ecuInfo?.reflag} — kembalikan ke 0000 (serial/tanggal tidak disentuh)` : "Aktif hanya kalau READ INFO menunjukkan reprog flag bukan 0000"}
                          className="h-[44px] px-2 rounded border border-amber-500/50 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 font-bold inline-flex items-center justify-center gap-2 text-[13px] disabled:opacity-40">
                          <RotateCcw size={18} /> Reset Flag
                        </button>
                        <button data-testid="yamaha-finalize" onClick={doFinalize} disabled={busy || !finalizeReady}
                          title={finalizeReady ? "Kembalikan reprog flag ke normal" : "Terkunci: jalankan READ INFO setelah WRITE dan pastikan motor hidup"}
                          className="h-[44px] px-2 rounded border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 font-bold inline-flex items-center justify-center gap-2 text-[13px] disabled:opacity-40">
                          <CheckCircle2 size={18} /> FLAG OK
                        </button>
                      </div>
                      <button data-testid="yamaha-write" onClick={doWrite} disabled={busy || !firmware}
                        className="shrink-0 h-[44px] rounded-sm border-0 bg-gradient-to-b from-sky-500 to-blue-700 hover:from-sky-400 hover:to-blue-600 text-white font-bold inline-flex items-center justify-center gap-3 text-[16px] disabled:opacity-40">
                        <Download size={22} /> {busy ? "WRITING" : "WRITE"}
                      </button>
                    </div>
                  </div>
                </div>
              </Panel>

              <div className="rounded-lg border border-sky-900/60 bg-[#0a0f17] shadow-lg flex items-center gap-4 shrink-0 px-6 py-3.5" data-testid="yamaha-speed-bar">
                <span className="font-black uppercase text-white shrink-0 text-[19px] max-w-[420px] truncate" data-testid="yamaha-proc-status">{procStatus || "Process:"}</span>
                <div className="flex-1 rounded-sm bg-[#141b26] border border-white/10 overflow-hidden h-[22px]">
                  <div className="h-full bg-gradient-to-r from-blue-700 via-sky-500 to-cyan-300 transition-[width] duration-300" style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
                </div>
                <span className="font-black text-white shrink-0 min-w-[76px] text-right text-[21px]" data-testid="yamaha-speed-pct">{Math.round(progress)} %</span>
              </div>
            </>
          )}

          {tab === "diag" && (
            <Panel className="flex-1 min-h-0 flex flex-col">
              <PanelHead icon={<ScanSearch size={24} />} title="DIAGNOSTIC" />
              <div data-testid="yamaha-diag-empty" className="flex-1" />
            </Panel>
          )}

          {tab === "info" && (
            <Panel className="flex-1 min-h-0 flex flex-col overflow-hidden">
              <PanelHead icon={<Hash size={24} />} title="DATABASE PART NUMBER YAMAHA" />
              <div className="p-4 flex flex-col gap-3 flex-1 min-h-0">
                <div className="flex gap-2">
                  <input data-testid="yamaha-partdb-search" value={hwQuery} onChange={(e) => setHwQuery(e.target.value)}
                    placeholder="Cari Software Id (mis. 12688000) atau part number (mis. BJ8-H591A)"
                    className="h-10 flex-1 rounded-[3px] border border-[#6b7280] bg-[#101722] px-3 text-[14px] font-mono text-white outline-none focus:border-sky-500" />
                  <button data-testid="yamaha-partdb-export" onClick={exportPartDb}
                    className="h-10 shrink-0 rounded-[3px] border border-emerald-500/60 bg-emerald-900/30 px-4 text-[13px] font-semibold text-emerald-200 transition-colors hover:bg-emerald-800/40">
                    EXPORT EXCEL
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-auto rounded border border-[#3b4756]" data-testid="yamaha-partdb-table">
                  <table className="w-full text-left border-collapse text-[14px] font-mono">
                    <thead className="sticky top-0"><tr className="bg-[#06182e] text-white"><th className="px-3 py-2">SOFTWARE ID (1A 94)</th><th className="px-3 py-2">PART NUMBER</th></tr></thead>
                    <tbody>
                      {hwHits.map(([k, v]) => (
                        <tr key={k} className="border-t border-[#1b2735] hover:bg-sky-900/20" data-testid={`yamaha-part-${k}`}>
                          <td className="px-3 py-1.5 text-sky-300">{k}</td><td className="px-3 py-1.5 text-slate-100">{v}</td>
                        </tr>
                      ))}
                      {!hwHits.length && <tr><td colSpan={2} className="px-3 py-3 text-slate-400">Tidak ada hasil.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </Panel>
          )}
        </div>

        <div className="relative grid grid-cols-3 gap-0 rounded-lg overflow-hidden border border-sky-900/60 bg-[#0a0f17] shrink-0" data-testid="yamaha-bottom-tabs">
          {TABS.map((t) => { const TabIcon = t.icon; return (
            <button key={t.id} data-testid={`yamaha-tab-${t.id}`} onClick={() => !busy && setTab(t.id)} disabled={busy && tab !== t.id}
              style={tab === t.id ? { clipPath: "polygon(0 0, 100% 0, calc(100% - 18px) 100%, 0 100%)" } : undefined}
              className={`relative px-2 min-h-[52px] py-1.5 inline-flex items-center justify-center gap-2 text-[14px] font-medium uppercase transition-colors ${tab === t.id ? "bg-gradient-to-r from-blue-700 to-sky-600 text-white" : "bg-transparent text-slate-300 hover:bg-white/5 hover:text-white"}`}>
              <TabIcon size={18} /> {t.label}
            </button>
          ); })}
        </div>
      </div>
    </div>
  );
}

const BASE_W = 1440, BASE_H = 880;
const IS_ANDROID = typeof navigator !== "undefined" && /Android|TiroTunerApp/i.test(navigator.userAgent || "");
const calcFit = () => {
  const vw = window.innerWidth, vh = window.innerHeight;
  const narrow = vw < 820 && vh > vw;
  const scale = narrow ? 1 : IS_ANDROID ? Math.min(vw / BASE_W, vh / BASE_H) : Math.min(1.15, (vw * 0.88) / BASE_W, (vh * 0.9) / BASE_H);
  return { vw, vh, narrow, scale };
};

export default function YamahaFlashtools({ embedded = false, onClose }) {
  const navigate = useNavigate();
  const [fit, setFit] = useState(calcFit);
  useEffect(() => {
    const on = () => setFit(calcFit());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("orientationchange", on); };
  }, []);
  if (embedded) return <YamahaFlashtoolsPanel onClose={onClose} />;
  const full = IS_ANDROID && !fit.narrow;
  const style = fit.narrow ? undefined : full ? { width: fit.vw / fit.scale, height: fit.vh / fit.scale, zoom: fit.scale } : { width: BASE_W, height: BASE_H, zoom: fit.scale };
  const box = fit.narrow ? "w-screen min-h-[100dvh] rounded-none" : full ? "rounded-none overflow-hidden border-0" : "rounded-xl overflow-hidden ring-1 ring-sky-500/80 shadow-[0_0_10px_rgba(56,165,255,0.55),0_30px_80px_rgba(0,0,0,0.8)]";
  return (
    <div className={`fixed inset-0 z-[200] ${full ? "bg-[#0F1015]" : "bg-black/80 backdrop-blur-sm"} ${fit.narrow ? "ft-narrow overflow-y-auto p-0" : "flex items-center justify-center"}`} data-testid="yamaha-page-frame">
      <div style={style} className={`relative border border-white/10 bg-[#05080e] shadow-2xl ${box}`}>
        <YamahaFlashtoolsPanel onClose={() => (onClose ? onClose() : navigate(window.tiroDesktopApp ? "/workspace" : "/"))} />
      </div>
    </div>
  );
}
